import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import ReactHtmlParser from 'react-html-parser';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import {
  FaHandsHelping, FaBullseye, FaLightbulb, FaUsers, FaHeart, FaGlobe,
  FaHome, FaUtensils, FaStar, FaAward, FaHandHoldingHeart, FaQuoteLeft,
  FaCheckCircle, FaLeaf, FaBalanceScale, FaShieldAlt, FaBrain,
  FaArrowRight, FaImage, FaProjectDiagram,
} from 'react-icons/fa';
import aboutFallback from '../../img/about.jpg';

/* ── Skeleton ──────────────────────────────────────────────── */
const Sk = ({ className = '' }) => (
  <div className={`animate-pulse bg-neutral-200 rounded-xl ${className}`} />
);

/* ── Icon helpers ──────────────────────────────────────────── */
const getCountIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('people') || n.includes('refugee') || n.includes('youth') || n.includes('family')) return FaUsers;
  if (n.includes('donation') || n.includes('help') || n.includes('support')) return FaHandHoldingHeart;
  if (n.includes('country') || n.includes('nation') || n.includes('global') || n.includes('communit')) return FaGlobe;
  if (n.includes('home') || n.includes('house') || n.includes('shelter')) return FaHome;
  if (n.includes('food') || n.includes('meal') || n.includes('nutrition')) return FaUtensils;
  if (n.includes('love') || n.includes('care') || n.includes('compassion')) return FaHeart;
  if (n.includes('year') || n.includes('star') || n.includes('excellent')) return FaStar;
  if (n.includes('award') || n.includes('recognition')) return FaAward;
  return FaUsers;
};

const getCoreValueIcon = (title = '', index = 0) => {
  const t = title.toLowerCase();
  if (t.includes('compassion') || t.includes('humanity')) return FaHeart;
  if (t.includes('youth') || t.includes('leadership')) return FaUsers;
  if (t.includes('inclusion') || t.includes('equity')) return FaBalanceScale;
  if (t.includes('peace') || t.includes('cohesion')) return FaShieldAlt;
  if (t.includes('resilience') || t.includes('empowerment')) return FaStar;
  if (t.includes('integrity') || t.includes('accountability')) return FaBrain;
  if (t.includes('environment') || t.includes('sustain')) return FaLeaf;
  const fallback = [FaHeart, FaUsers, FaBalanceScale, FaShieldAlt, FaStar, FaBrain];
  return fallback[index % fallback.length];
};

/* ── Palettes ──────────────────────────────────────────────── */
const CV_PALETTE = [
  { accent: '#1B2B5C', light: '#EEF1F9' },
  { accent: '#3DAA6B', light: '#E8F8EE' },
  { accent: '#2E8B54', light: '#D4F0E1' },
  { accent: '#2D4E96', light: '#E4EAF6' },
  { accent: '#237A4B', light: '#D4F0E1' },
  { accent: '#1B6039', light: '#C5EDDA' },
];

const STAT_COLORS = [
  { bg: '#E8F8EE', icon: '#3DAA6B', num: '#237A4B', label: 'text-secondary-500' },
  { bg: '#EEF1F9', icon: '#1B2B5C', num: '#142147', label: 'text-primary-700'   },
  { bg: '#E8F8EE', icon: '#3DAA6B', num: '#237A4B', label: 'text-secondary-500' },
  { bg: '#E8F8EE', icon: '#3DAA6B', num: '#237A4B', label: 'text-secondary-500' },
];

const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'];

const TABS = [
  { id: 'about',   label: 'Our Story', Icon: FaHandsHelping },
  { id: 'mission', label: 'Mission',   Icon: FaBullseye     },
  { id: 'vision',  label: 'Vision',    Icon: FaLightbulb    },
];

const TAB_TITLE = {
  about:   'Making a Difference Since 2019',
  mission: 'Our Core Mission',
  vision:  'Our Vision for the Future',
};

const FALLBACK_STATS = [
  { Icon: FaUsers,        label: 'Lives Impacted',     value: 500, suffix: '+' },
  { Icon: FaGlobe,        label: 'Communities Served', value: 15,  suffix: '+' },
  { Icon: FaHeart,        label: 'Years of Service',   value: 5,   suffix: '+' },
  { Icon: FaHandsHelping, label: 'Volunteers',         value: 100, suffix: '+' },
];

/* ══════════════════════════════════════════════════════════════
   AboutUs — Full page component (/about route)
   Sections: Stats → Story/Mission/Vision (with image) → Core Values → Objectives
══════════════════════════════════════════════════════════════ */
class AboutUs extends Component {
  constructor() {
    super();
    this.state = {
      about:        '',
      mission:      '',
      vision:       '',
      whatwhywedo:  '',
      aboutImage:       '',   // desktop: 4:5 portrait  (800×1000)
      aboutImageMobile: '',   // mobile : 3:2 landscape (900×600)
      activeTab:        'about',
      countData:    [],
      coreValues:   [],
      objectives:   [],
      loadingAbout: true,
      loadingCount: true,
      loadingExtra: true,
      imgLoaded:    false,
      imgError:     false,
    };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.Abouthome).then(result => {
      if (result && result[0]) {
        this.setState({
          about:        result[0]['about']        || '',
          mission:      result[0]['mission']      || '',
          vision:       result[0]['vision']       || result[0]['number'] || '',
          whatwhywedo:  result[0]['whatwhywedo']  || '',
          aboutImage:       result[0]['about_image2']       || '',
          aboutImageMobile: result[0]['about_image_mobile'] || '',
          loadingAbout:     false,
        });
      } else {
        this.setState({ loadingAbout: false });
      }
    }).catch(() => this.setState({ loadingAbout: false }));

    IRestClient.GetRequest(AppUrl.Counthome).then(result => {
      this.setState({ countData: result || [], loadingCount: false });
    }).catch(() => this.setState({ loadingCount: false }));

    Promise.all([
      IRestClient.GetRequest(AppUrl.CoreValues),
      IRestClient.GetRequest(AppUrl.Objectives),
    ]).then(([cv, obj]) => {
      this.setState({ coreValues: cv || [], objectives: obj || [], loadingExtra: false });
    }).catch(() => this.setState({ loadingExtra: false }));
  }

  handleTabChange = (tab) => this.setState({ activeTab: tab });

  render() {
    const {
      about, mission, vision, whatwhywedo,
      aboutImage, aboutImageMobile,
      activeTab, countData, coreValues, objectives,
      loadingAbout, loadingCount, loadingExtra,
      imgLoaded, imgError,
    } = this.state;

    // Desktop source: 4:5 portrait (800×1000)
    const imgDesktop = aboutImage && !imgError ? aboutImage : aboutFallback;
    // Mobile source : 3:2 landscape (900×600) — falls back to desktop portrait
    const imgMobile  = aboutImageMobile && !imgError ? aboutImageMobile : imgDesktop;
    const showSkel   = aboutImage && !imgLoaded;
    const stats   = countData.length > 0 ? countData.slice(0, 4) : FALLBACK_STATS;

    return (
      <div>

        {/* ══════════════════════════════════════════════════════
            1. IMPACT STATS BANNER
        ══════════════════════════════════════════════════════ */}
        <section className="bg-white py-10 sm:py-12 border-b border-neutral-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {loadingCount
                ? [0,1,2,3].map(i => (
                    <div key={i} className="flex flex-col items-center gap-3 p-4">
                      <Sk className="w-12 h-12" />
                      <Sk className="h-6 w-14" />
                      <Sk className="h-3 w-20" />
                    </div>
                  ))
                : stats.map((count, i) => {
                    const Icon  = count.count_name ? getCountIcon(count.count_name) : count.Icon;
                    const value = count.count_number != null ? parseInt(String(count.count_number)) || 0 : count.value;
                    const label = count.count_name || count.label;
                    const suf   = count.suffix || '+';
                    const col   = STAT_COLORS[i % STAT_COLORS.length];
                    return (
                      <motion.div
                        key={i}
                        className="flex flex-col items-center text-center rounded-2xl py-5 px-3"
                        style={{ background: col.bg }}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}>
                        <div
                          className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3"
                          style={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                          <Icon style={{ color: col.icon, fontSize: '1.1rem' }} />
                        </div>
                        <div className="flex items-baseline gap-px leading-none mb-1">
                          <CountUp start={0} end={value} duration={2.5}>
                            {({ countUpRef, start }) => (
                              <VisibilitySensor onChange={start} partialVisibility>
                                <span
                                  ref={countUpRef}
                                  className="text-2xl sm:text-3xl font-black"
                                  style={{ color: col.num }}
                                />
                              </VisibilitySensor>
                            )}
                          </CountUp>
                          <span className="text-xl sm:text-2xl font-black" style={{ color: col.num }}>{suf}</span>
                        </div>
                        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest leading-tight">
                          {label}
                        </p>
                      </motion.div>
                    );
                  })
              }
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════
            2. STORY / MISSION / VISION  +  IMAGE
        ══════════════════════════════════════════════════════ */}
        <section
          className="py-14 sm:py-20 lg:py-24 overflow-hidden"
          style={{ background: 'linear-gradient(160deg,#EEF1F9 0%,#ffffff 55%,#E8F8EE 100%)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Section heading — centred above grid */}
            <motion.div
              className="text-center mb-10 sm:mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>
              <span
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3"
                style={{ background: '#E8F8EE', color: '#237A4B', border: '1px solid #94DDB8' }}>
                <FaCheckCircle style={{ color: '#3DAA6B', fontSize: '0.65rem' }} />
                About SOYAN
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-primary-900 leading-tight mb-2">
                Empowering Youth{' '}
                <span className="text-secondary-600">for a Better Tomorrow</span>
              </h2>
              <p className="text-neutral-500 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
                A youth-led non-profit registered in Uganda, delivering programmes in education,
                entrepreneurship, health, peacebuilding, and community development since 2019.
              </p>
            </motion.div>

            {/* ── Two-column grid ───────────────────────────── */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start lg:items-center">

              {/* LEFT — Image panel (order-2 on mobile → shows below content) */}
              <motion.div
                className="relative order-2 lg:order-1"
                initial={{ opacity: 0, x: -44 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.72, ease: 'easeOut' }}>

                {/* Green accent spine */}
                <div
                  className="hidden sm:block absolute -left-3 lg:-left-4 top-8 bottom-8 w-1.5 rounded-full z-20"
                  style={{ background: 'linear-gradient(180deg,#237A4B 0%,#3DAA6B 55%,#94DDB8 100%)' }}
                />

                {/* Decorative orb */}
                <div
                  className="absolute -top-5 -right-5 w-28 h-28 rounded-full opacity-20 blur-2xl pointer-events-none"
                  style={{ background: '#2E8B54' }}
                />

                {/*
                    Responsive aspect ratios:
                      mobile  <640px   → 3:2  landscape  (matches 900×600 server crop)
                      sm      640px+   → 4:3  transitional
                      lg      1024px+  → 4:5  portrait   (matches 800×1000 server crop)
                */}
                <style>{`
                  .about-page-img-frame { aspect-ratio: 3/2; }
                  @media (min-width: 640px)  { .about-page-img-frame { aspect-ratio: 4/3; } }
                  @media (min-width: 1024px) { .about-page-img-frame { aspect-ratio: 4/5; } }
                `}</style>

                <div className="about-page-img-frame relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl sm:ml-2">

                  {/* Skeleton while loading */}
                  {showSkel && (
                    <div className="absolute inset-0 animate-pulse bg-neutral-200 z-10" />
                  )}

                  {/* ── Picture element — serves the right crop per device ── */}
                  {/* Mobile (<640px): 3:2 landscape (900×600)                 */}
                  {/* Tablet + Desktop (640px+): 4:5 portrait (800×1000)       */}
                  <picture
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
                    <source media="(max-width: 639px)" srcSet={imgMobile} />
                    <img
                      src={imgDesktop}
                      alt="SOYAN — empowering Somali youth"
                      loading="lazy"
                      decoding="async"
                      onLoad={() => this.setState({ imgLoaded: true })}
                      onError={() => this.setState({ imgError: true, imgLoaded: true })}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        display: 'block',
                        opacity: imgLoaded || !aboutImage ? 1 : 0,
                        transition: 'opacity 0.45s ease',
                      }}
                    />
                  </picture>

                  {/* Error fallback */}
                  {imgError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-neutral-100 text-neutral-300 z-20">
                      <FaImage className="text-4xl mb-2" />
                      <span className="text-xs font-medium">Image unavailable</span>
                    </div>
                  )}

                  {/* Bottom gradient */}
                  <div
                    className="absolute inset-0 pointer-events-none z-10"
                    style={{ background: 'linear-gradient(to top,rgba(16,42,67,0.72) 0%,rgba(16,42,67,0.10) 42%,transparent 68%)' }}
                  />

                  {/* Est. 2018 badge */}
                  <motion.div
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20"
                    initial={{ opacity: 0, scale: 0.75 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.55, duration: 0.4, type: 'spring', stiffness: 220 }}>
                    <div
                      className="rounded-xl sm:rounded-2xl px-2.5 py-1.5 sm:px-3 sm:py-2 shadow-lg"
                      style={{ background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(10px)' }}>
                      <p className="text-[10px] font-black text-primary-700 uppercase tracking-widest leading-none">Est.</p>
                      <p className="text-base sm:text-lg font-black text-secondary-600 leading-none">2019</p>
                    </div>
                  </motion.div>

                  {/* Floating stat cards */}
                  <motion.div
                    className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 z-20"
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.65, duration: 0.5 }}>
                    <div className="grid grid-cols-2 gap-2">
                      <div
                        className="rounded-xl sm:rounded-2xl p-2.5 sm:p-3 shadow-lg"
                        style={{ background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(10px)' }}>
                        <p className="text-lg sm:text-xl font-black text-primary-900 leading-none">500+</p>
                        <p className="text-[11px] sm:text-xs font-semibold mt-0.5 text-neutral-500">Youth Reached</p>
                      </div>
                      <div
                        className="rounded-xl sm:rounded-2xl p-2.5 sm:p-3 shadow-lg"
                        style={{ background: 'rgba(45,170,107,0.90)', backdropFilter: 'blur(10px)' }}>
                        <p className="text-lg sm:text-xl font-black text-white leading-none">15+</p>
                        <p className="text-[11px] sm:text-xs font-semibold mt-0.5 text-green-100">Communities</p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Dot grid decoration */}
                <div
                  className="hidden sm:block absolute -bottom-4 -right-3 w-24 h-24 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: 'radial-gradient(circle,#3DAA6B 1.5px,transparent 1.5px)',
                    backgroundSize: '9px 9px',
                  }}
                />
              </motion.div>

              {/* RIGHT — Tabs + content (order-1 on mobile → shows above image) */}
              <motion.div
                className="order-1 lg:order-2 flex flex-col"
                initial={{ opacity: 0, x: 44 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.72, ease: 'easeOut', delay: 0.1 }}>

                {loadingAbout ? (
                  <div className="space-y-3">
                    <div className="flex gap-1.5">
                      <Sk className="h-11 flex-1" /><Sk className="h-11 flex-1" /><Sk className="h-11 flex-1" />
                    </div>
                    <Sk className="h-52 w-full" />
                  </div>
                ) : (
                  <>
                    {/* Pill tab nav */}
                    <div className="flex bg-neutral-100 rounded-2xl p-1 mb-4 gap-1">
                      {TABS.map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          onClick={() => this.handleTabChange(id)}
                          className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-2.5 sm:py-3 px-1 sm:px-3 rounded-xl text-[11px] sm:text-sm font-semibold transition-all duration-300 ${
                            activeTab === id
                              ? 'bg-white shadow-md text-primary-800'
                              : 'text-neutral-500 hover:text-primary-700'
                          }`}>
                          <Icon className={`text-xs sm:text-sm flex-shrink-0 ${activeTab === id ? 'text-secondary-600' : ''}`} />
                          <span className="truncate">{label}</span>
                        </button>
                      ))}
                    </div>

                    {/* Tab content card — full text, no truncation */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.28 }}
                        className="bg-white rounded-2xl border border-neutral-100 shadow-lg p-5 sm:p-6 lg:p-7">

                        <FaQuoteLeft style={{ color: '#94DDB8', fontSize: '1rem', marginBottom: '8px' }} />

                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-primary-900 mb-3 leading-snug">
                          {TAB_TITLE[activeTab]}
                        </h3>

                        <div className="text-neutral-600 text-sm sm:text-base leading-relaxed prose max-w-none">
                          {ReactHtmlParser(
                            activeTab === 'about'   ? (about   || '') :
                            activeTab === 'mission' ? (mission || '') :
                            (vision || '')
                          )}
                          {activeTab === 'vision' && whatwhywedo && (
                            <div className="mt-3 pt-3 border-t border-neutral-100">
                              {ReactHtmlParser(whatwhywedo)}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </AnimatePresence>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-5">
                      {/* Donate Now — hidden until payment goes live */}
                      {false && (
                        <Link
                          to="/donate"
                          className="inline-flex items-center justify-center gap-2 font-bold py-3 px-6 rounded-xl text-sm transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 text-white"
                          style={{ background: '#237A4B' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#1B6039'}
                          onMouseLeave={e => e.currentTarget.style.background = '#237A4B'}>
                          <FaHandHoldingHeart /> Donate Now
                        </Link>
                      )}
                      <Link
                        to="/projects"
                        className="inline-flex items-center justify-center gap-2 font-semibold text-sm border border-neutral-200 rounded-xl py-3 px-6 bg-white text-primary-700 transition-all duration-300 group hover:border-secondary-300 hover:text-secondary-700 hover:bg-secondary-50">
                        <FaProjectDiagram className="text-xs" />
                        Our Projects
                        <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════
            3. CORE VALUES — animated vertical timeline
        ══════════════════════════════════════════════════════ */}
        <section
          className="py-12 sm:py-16 lg:py-20 overflow-hidden"
          style={{ background: 'linear-gradient(160deg,#EEF1F9 0%,#ffffff 60%,#E8F8EE 100%)' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6">

            <motion.div
              className="text-center mb-8 sm:mb-10"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>
              <motion.span
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4"
                style={{ background: '#E8F8EE', color: '#237A4B', border: '1px solid #94DDB8' }}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}>
                <FaCheckCircle style={{ color: '#3DAA6B' }} /> What We Stand For
              </motion.span>
              <h2 className="text-2xl sm:text-3xl font-black text-primary-900 leading-tight mb-2">
                Our Core{' '}
                <span style={{ color: '#3DAA6B' }}>
                  Values
                </span>
              </h2>
              <p className="text-neutral-500 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
                The principles that guide every decision, every programme, and every interaction with the communities we serve.
              </p>
              <motion.div
                className="mx-auto mt-4 h-0.5 rounded-full"
                style={{ background: 'linear-gradient(90deg,#3DAA6B,#1B2B5C)', width: 0 }}
                whileInView={{ width: 64 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </motion.div>

            {loadingExtra ? (
              <div className="space-y-3">
                {[0,1,2,3,4].map(i => <Sk key={i} className="h-20 w-full" />)}
              </div>
            ) : coreValues.length > 0 ? (
              <div className="relative">
                {/* Vertical spine */}
                <motion.div
                  className="absolute top-6 bottom-6 w-0.5 rounded-full"
                  style={{
                    left: '23px',
                    background: 'linear-gradient(180deg,#3DAA6B 0%,#1B2B5C 50%,#2D4E96 100%)',
                    transformOrigin: 'top',
                  }}
                  initial={{ scaleY: 0 }}
                  whileInView={{ scaleY: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: 'easeOut' }}
                />

                <div className="space-y-3">
                  {coreValues.map((cv, i) => {
                    const Icon = getCoreValueIcon(cv.title, i);
                    const pal  = CV_PALETTE[i % CV_PALETTE.length];
                    return (
                      <motion.div
                        key={cv.id || i}
                        className="relative flex items-start gap-3 sm:gap-4 group"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.09, ease: 'easeOut' }}>

                        {/* Icon badge */}
                        <div className="relative flex-shrink-0 z-10">
                          <motion.div
                            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
                            style={{ background: pal.light, border: `2px solid ${pal.accent}22` }}
                            whileHover={{ rotate: [0, -5, 5, 0], scale: 1.1, transition: { duration: 0.35 } }}>
                            <Icon style={{ color: pal.accent, fontSize: '1.15rem' }} />
                          </motion.div>
                        </div>

                        {/* Content card */}
                        <motion.div
                          className="flex-1 rounded-xl p-3.5 sm:p-4 cursor-default"
                          style={{ background: '#ffffff', border: '1px solid #f1f5f9', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}
                          whileHover={{ y: -2, boxShadow: `0 10px 28px ${pal.accent}15`, borderColor: `${pal.accent}33` }}
                          transition={{ duration: 0.2 }}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <span
                              className="text-xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                              style={{ background: `${pal.accent}15`, color: pal.accent }}>
                              {ROMAN[i] ? `${ROMAN[i]}.` : `${i + 1}.`}
                            </span>
                            <h4 className="text-sm sm:text-base font-bold text-primary-900 leading-snug">
                              {cv.title}
                            </h4>
                          </div>
                          <p className="text-xs sm:text-sm text-neutral-500 leading-relaxed">
                            {cv.description}
                          </p>
                          <motion.div
                            className="mt-3 h-0.5 rounded-full"
                            style={{ background: `linear-gradient(90deg,${pal.accent},transparent)`, originX: 0 }}
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.55, delay: i * 0.09 + 0.25 }}
                          />
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-center text-neutral-400 text-sm py-8">
                Core values will appear here once added from the admin panel.
              </p>
            )}
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════
            4. OBJECTIVES — dark numbered reveal list
        ══════════════════════════════════════════════════════ */}
        <section
          className="py-12 sm:py-16 lg:py-20 overflow-hidden"
          style={{ background: '#0D1630' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6">

            <motion.div
              className="text-center mb-8 sm:mb-10"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>
              <motion.span
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4"
                style={{ background: 'rgba(46,139,84,0.15)', color: '#2E8B54', border: '1px solid rgba(46,139,84,0.3)' }}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}>
                <FaArrowRight style={{ color: '#2E8B54' }} /> What We Aim to Achieve
              </motion.span>
              <h2 className="text-2xl sm:text-3xl font-black leading-tight mb-2 text-white">
                Objectives of the{' '}
                <span style={{ color: '#2E8B54' }}>
                  Organisation
                </span>
              </h2>
              <p className="text-slate-400 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
                Clear, measurable goals that focus our work and ensure we deliver lasting impact for every community we serve.
              </p>
              <motion.div
                className="mx-auto mt-4 h-0.5 rounded-full"
                style={{ background: 'linear-gradient(90deg,#2E8B54,#1B2B5C)', width: 0 }}
                whileInView={{ width: 64 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </motion.div>

            {loadingExtra ? (
              <div className="space-y-3">
                {[0,1,2,3].map(i => (
                  <div
                    key={i}
                    className="animate-pulse h-20 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  />
                ))}
              </div>
            ) : objectives.length > 0 ? (
              <div className="space-y-3">
                {objectives.map((obj, i) => (
                  <motion.div
                    key={obj.id || i}
                    className="group relative flex items-start gap-3 sm:gap-4 rounded-xl p-4 sm:p-5 cursor-default overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1, ease: 'easeOut' }}
                    whileHover={{ background: 'rgba(46,139,84,0.08)', borderColor: 'rgba(46,139,84,0.3)' }}>

                    {/* Ghost number */}
                    <span
                      className="absolute right-4 top-1/2 font-black select-none pointer-events-none leading-none"
                      style={{ fontSize: 'clamp(2.5rem,8vw,4rem)', color: 'rgba(255,255,255,0.03)', transform: 'translateY(-50%)' }}>
                      {i + 1}
                    </span>

                    {/* Number badge */}
                    <div
                      className="relative z-10 flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                      style={{ background: 'linear-gradient(135deg,#1B2B5C,#2D4E96)', boxShadow: '0 3px 12px rgba(27,43,92,0.35)' }}>
                      <span className="text-white text-xs font-black">{ROMAN[i] ? `${ROMAN[i]}.` : `${i + 1}.`}</span>
                    </div>

                    {/* Text */}
                    <div className="relative z-10 flex-1 flex items-start gap-2 sm:gap-2.5 pt-0.5">
                      <motion.div
                        className="flex-shrink-0 mt-0.5"
                        initial={{ scale: 0, rotate: -90 }}
                        whileInView={{ scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.1 + 0.25, type: 'spring', stiffness: 260 }}>
                        <FaCheckCircle style={{ color: '#3DAA6B', fontSize: '0.85rem' }} />
                      </motion.div>
                      <p className="text-slate-300 text-sm sm:text-base leading-relaxed group-hover:text-white transition-colors duration-300">
                        {obj.objective_text}
                      </p>
                    </div>

                    {/* Left accent bar */}
                    <motion.div
                      className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
                      style={{ background: 'linear-gradient(180deg,#2E8B54,#1B2B5C)', scaleY: 0, originY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 + 0.15 }}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 text-sm py-8">
                Objectives will appear here once added from the admin panel.
              </p>
            )}

          </div>
        </section>

      </div>
    );
  }
}

export default AboutUs;
